/**
 * `<caos-skeleton-block>` — a shape-preserving placeholder while data loads.
 * (Catalog: Data display › SkeletonBlock — a shape-preserving placeholder while data loads.)
 *
 * A neutral surface block that shimmers. `variant` sets a preset shape:
 * `line` (default, text line) · `title` (taller line) · `block` (a filled region) ·
 * `circle` (avatar). `w` / `h` override the size with any CSS length, and `radius`
 * follows the token scale. The shimmer respects `prefers-reduced-motion`: it falls
 * back to a static pulse-free tint so it never animates for a reader who opted out.
 * Styling seam: `::part(block)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

export type CaosSkeletonVariant = 'line' | 'title' | 'block' | 'circle';

const sheet = makeSheet(`
  :host { display: block; }
  :host([variant="line"]), :host([variant="title"]), :host(:not([variant])) { display: block; }
  :host([variant="circle"]) { display: inline-block; }

  .block {
    width: var(--sk-w, 100%);
    height: var(--sk-h, 14px);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface-2);
    position: relative;
    overflow: hidden;
  }

  /* variant presets (overridden by --sk-w/--sk-h when set) */
  :host([variant="title"]) .block { height: var(--sk-h, 22px); width: var(--sk-w, 60%); border-radius: var(--caos-radius-sm); }
  :host([variant="block"]) .block { height: var(--sk-h, 96px); width: var(--sk-w, 100%); border-radius: var(--caos-radius); }
  :host([variant="circle"]) .block { height: var(--sk-h, 40px); width: var(--sk-w, 40px); border-radius: var(--caos-radius-pill); }

  /* shimmer sweep — a moving translucent highlight over the base tint */
  .block::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      color-mix(in srgb, var(--caos-color-surface) 55%, transparent) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    animation: caos-skeleton-sweep 1.3s ease-in-out infinite;
  }

  @keyframes caos-skeleton-sweep {
    100% { transform: translateX(100%); }
  }

  /* reduced motion — no sweep; a gentle, non-moving tint difference only */
  @media (prefers-reduced-motion: reduce) {
    .block::after { animation: none; background: none; }
    .block { background: var(--caos-color-surface-2); }
  }
`);

export class CaosSkeletonBlock extends CaosElement {
  static get observedAttributes(): string[] {
    return ['w', 'h', 'radius'];
  }

  private block: HTMLDivElement;

  constructor() {
    super(sheet);
    this.block = document.createElement('div');
    this.block.className = 'block';
    this.block.setAttribute('part', 'block');
    this.block.setAttribute('aria-hidden', 'true');
    this.root.append(this.block);
  }

  connectedCallback(): void {
    // A loading placeholder is announced as busy to assistive tech.
    if (!this.hasAttribute('role')) this.setAttribute('aria-busy', 'true');
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    const w = this.getAttribute('w');
    const h = this.getAttribute('h');
    const radius = this.getAttribute('radius');
    if (w !== null) this.style.setProperty('--sk-w', w);
    else this.style.removeProperty('--sk-w');
    if (h !== null) this.style.setProperty('--sk-h', h);
    else this.style.removeProperty('--sk-h');
    if (radius !== null) this.block.style.borderRadius = radius;
    else this.block.style.removeProperty('border-radius');
  }
}

define('caos-skeleton-block', CaosSkeletonBlock);

declare global {
  interface HTMLElementTagNameMap {
    'caos-skeleton-block': CaosSkeletonBlock;
  }
}
